import { useEffect, useRef, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { BookOpen, Loader2, Search, X } from 'lucide-react';
import { listDecisions, getOptions } from '@generated/server/decisions';
import { useSession } from '@generated/components/session';
import { DecisionCard } from '@generated/components/DecisionCard';
import { DecisionDetailModal } from '@generated/components/DecisionDetailModal';
import type { Decision } from '@generated/lib/decision-utils';

type Lib = 'all' | 'itp' | 'sd';
type Cursors = { itp: string | null; sd: string | null };
const FILTERS = [
  ['document', 'Document type'], ['sport', 'Sport'], ['province', 'Province'], ['level', 'Level'], ['finding', 'Finding'], ['complaintType', 'Complaint type'],
] as const;
type FilterKey = (typeof FILTERS)[number][0];

export default function App() {
  const { sid, user } = useSession();
  const [lib, setLib] = useState<Lib>('all');
  const [search, setSearch] = useState('');
  const term = useDebounce(search, 300);
  const [filters, setFilters] = useState<Partial<Record<FilterKey, string>>>({});
  const [options, setOptions] = useState<Record<string, string[]>>({});
  const [items, setItems] = useState<Decision[]>([]);
  const [cursors, setCursors] = useState<Cursors | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<Decision | null>(null);
  const first = useRef(true);

  useEffect(() => {
    getOptions({ data: { sid, lib } }).then((r) => setOptions(r?.options ?? {})).catch((e) => console.error(e));
  }, [sid, lib]);

  useEffect(() => {
    let active = true;
    if (first.current) setLoading(true); else setRefetching(true);
    setError('');
    listDecisions({ data: { sid, lib, filters: { ...filters, search: term || undefined } } })
      .then((r) => { if (!active) return; setItems(r?.items ?? []); setCursors(r?.cursors ?? null); setHasMore(!!r?.hasMore); })
      .catch((e: Error) => { console.error(e); if (active) setError(e.message || 'Could not load decisions.'); })
      .finally(() => { if (active) { setLoading(false); setRefetching(false); first.current = false; } });
    return () => { active = false; };
  }, [sid, lib, term, filters]);

  async function loadMore() {
    if (!cursors) return;
    setLoadingMore(true);
    try {
      const r = await listDecisions({ data: { sid, lib, filters: { ...filters, search: term || undefined }, cursors } });
      setItems((p) => [...p, ...(r?.items ?? [])]);
      setCursors(r?.cursors ?? null);
      setHasMore(!!r?.hasMore);
    } catch (e) {
      console.error(e);
      setError('Could not load more decisions.');
    } finally {
      setLoadingMore(false);
    }
  }

  const libs = user?.libraries ?? [];
  const activeFilters = Object.values(filters).filter(Boolean).length;

  return (
    <div>
      <div className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">Precedent library</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-secondary sm:text-4xl">Published decisions</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Search adjudicative decisions by case reference or summary text, then narrow by document, sport, province, level and outcome.</p>
      </div>

      <section className="glass-card mb-6 rounded-2xl p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="flex gap-1 rounded-full bg-[hsl(var(--track))] p-1">
            {(['all', ...libs] as Lib[]).map((l) => (
              <button key={l} onClick={() => setLib(l)} className={`rounded-full px-4 py-1.5 text-sm font-medium ${lib === l ? 'bg-card text-secondary shadow-sm' : 'text-muted-foreground'}`}>
                {l === 'all' ? 'All' : l === 'itp' ? 'ITP Hockey Canada' : 'Sport Dispute'}
              </button>
            ))}
          </div>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search case reference or summary…" className="glass-input h-11 w-full rounded-xl pl-9 pr-9 text-sm outline-none" />
            {refetching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-primary" />}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {FILTERS.map(([k, label]) => (
            <select key={k} aria-label={label} value={filters[k] ?? ''} onChange={(e) => setFilters((f) => ({ ...f, [k]: e.target.value || undefined }))} className="glass-input h-10 min-w-0 rounded-xl px-2 text-sm outline-none">
              <option value="">{label}</option>
              {(options[k] ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
        </div>
        {activeFilters > 0 && (
          <button onClick={() => setFilters({})} className="mt-3 flex items-center gap-1 text-xs font-medium text-primary"><X className="h-3 w-3" /> Clear {activeFilters} filter{activeFilters > 1 ? 's' : ''}</button>
        )}
      </section>

      {error && <div className="mb-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="glass-card h-48 animate-pulse rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="glass-card rounded-2xl py-20 text-center">
          <BookOpen className="mx-auto h-10 w-10 text-secondary/20" />
          <h2 className="mt-4 text-lg font-semibold text-secondary">No published decisions match</h2>
          <p className="mt-1 text-sm text-muted-foreground">Try a different search or clear some filters.</p>
        </div>
      ) : (
        <>
          <div className={`grid gap-4 md:grid-cols-2 xl:grid-cols-3 ${refetching ? 'opacity-60' : ''}`}>
            {items.map((d) => <DecisionCard key={d.key} d={d} onOpen={() => setOpen(d)} />)}
          </div>
          {hasMore && (
            <div className="mt-6 flex justify-center">
              <button onClick={loadMore} disabled={loadingMore} className="flex h-11 items-center gap-2 rounded-xl border border-border bg-card px-6 text-sm font-medium text-secondary hover:border-primary/40 disabled:opacity-60">
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />} Load more
              </button>
            </div>
          )}
        </>
      )}
      {open && <DecisionDetailModal decision={open} onClose={() => setOpen(null)} onUpdated={(u) => { setItems((p) => p.map((x) => (x.key === u.key ? u : x))); setOpen(u); }} />}
    </div>
  );
}
